import React, { useRef, useState } from 'react';
import { Link2, Upload, ImageOff, Loader2 } from 'lucide-react';
import { ImageCropperModal } from '../../ui/ImageCropperModal';
import { fetchImageFromUrl, blobToFile } from '../../../utils/imageCropUtils';

interface PersonagemPhotoPickerProps {
  previewUrl: string;
  onPhotoReady: (file: File, previewUrl: string) => void;
}

export const PersonagemPhotoPicker: React.FC<PersonagemPhotoPickerProps> = ({
  previewUrl,
  onPhotoReady
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [cropSource, setCropSource] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    setCropSource(URL.createObjectURL(file));
  };

  const handleDownloadFromUrl = async () => {
    if (!imageUrl.trim()) return;
    setDownloading(true);
    setError('');
    try {
      const { objectUrl } = await fetchImageFromUrl(imageUrl);
      setCropSource(objectUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao baixar imagem da URL.');
    } finally {
      setDownloading(false);
    }
  };

  const handleCropConfirm = (blob: Blob) => {
    const file = blobToFile(blob, `personagem-${Date.now()}.jpg`);
    onPhotoReady(file, URL.createObjectURL(blob));
    setCropSource(null);
    setImageUrl('');
  };

  return (
    <div className="flex items-start gap-4">
      <div className="w-24 h-24 rounded-2xl border border-slate-200 bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
        {previewUrl ? (
          <img src={previewUrl} alt="Pré-visualização" className="w-full h-full object-cover" />
        ) : (
          <ImageOff className="w-8 h-8 text-slate-300" />
        )}
      </div>

      <div className="flex-1 space-y-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Foto do Personagem</label>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-700 font-medium rounded-lg hover:bg-teal-100 transition-colors text-sm"
          >
            <Upload className="w-4 h-4" />
            Escolher arquivo
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Ou colar URL da imagem</label>
          <div className="flex gap-2">
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://exemplo.com/imagem.jpg"
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <button
              type="button"
              onClick={handleDownloadFromUrl}
              disabled={downloading || !imageUrl.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-900 transition-colors disabled:opacity-50"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              Baixar
            </button>
          </div>
          {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
        </div>
      </div>

      <ImageCropperModal
        imageSrc={cropSource}
        aspect={1}
        onCancel={() => setCropSource(null)}
        onConfirm={handleCropConfirm}
      />
    </div>
  );
};
